import React from 'react';
import { Tag, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';

// Central status → antd Tag color maps, so every governance page renders statuses
// identically. Colors follow the admin-portal convention (antd named colors).
const ERASURE_COLORS: Record<string, string> = {
  REQUESTED: 'gold',
  REVIEWED: 'blue',
  APPROVED: 'cyan',
  PROCESSING: 'geekblue',
  COMPLETED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
  BLOCKED_BY_LEGAL_HOLD: 'volcano',
};
const TENANT_LIFECYCLE_COLORS: Record<string, string> = {
  ACTIVE: 'green',
  SUSPENDED: 'orange',
  OFFBOARDING: 'gold',
  RETENTION_REVIEW: 'blue',
  RETENTION_LOCKED: 'geekblue',
  ANONYMIZED: 'purple',
  DELETED: 'red',
};
const EMPLOYEE_LIFECYCLE_COLORS: Record<string, string> = {
  ACTIVE: 'green',
  INACTIVE: 'default',
  TERMINATED: 'orange',
  ARCHIVED: 'blue',
  ERASURE_REQUESTED: 'gold',
  ANONYMIZED: 'purple',
  LEGAL_HOLD: 'volcano',
};
const DECISION_COLORS: Record<string, string> = {
  DUE: 'gold',
  NOT_DUE: 'default',
  BLOCKED_BY_LEGAL_HOLD: 'volcano',
  RETAIN_REQUIRED: 'blue',
  UNSUPPORTED: 'default',
};
const ACTION_COLORS: Record<string, string> = {
  RETAINED: 'blue',
  ANONYMIZED: 'purple',
  DELETED: 'red',
  SKIPPED: 'default',
  BLOCKED: 'volcano',
  DRY_RUN_EVALUATED: 'cyan',
  LEGAL_HOLD_CREATED: 'volcano',
  LEGAL_HOLD_RELEASED: 'green',
};
const RETENTION_ACTION_COLORS: Record<string, string> = {
  REVIEW: 'blue',
  ANONYMIZE: 'purple',
  DELETE: 'red',
  RETAIN: 'default',
};

const label = (s: string) => s.replace(/_/g, ' ');

export type BadgeKind =
  | 'erasure'
  | 'tenant_lifecycle'
  | 'employee_lifecycle'
  | 'dry_run_decision'
  | 'evidence_action'
  | 'retention_action';

const MAP: Record<BadgeKind, Record<string, string>> = {
  erasure: ERASURE_COLORS,
  tenant_lifecycle: TENANT_LIFECYCLE_COLORS,
  employee_lifecycle: EMPLOYEE_LIFECYCLE_COLORS,
  dry_run_decision: DECISION_COLORS,
  evidence_action: ACTION_COLORS,
  retention_action: RETENTION_ACTION_COLORS,
};

/** ComplianceStatusBadge — a status Tag whose color is consistent platform-wide. */
export const ComplianceStatusBadge: React.FC<{ kind: BadgeKind; value?: string | null }> = ({
  kind,
  value,
}) => {
  if (!value) return <Tag>—</Tag>;
  return <Tag color={MAP[kind][value] ?? 'default'}>{label(value)}</Tag>;
};

/**
 * LegalHoldBadge — high-priority marker. A record/subject under legal hold cannot be
 * erased, anonymized, or retention-purged; this makes that unmistakable.
 */
export const LegalHoldBadge: React.FC<{ active?: boolean; scope?: string[] }> = ({
  active,
  scope,
}) => {
  if (!active) return <Tag color="default">No hold</Tag>;
  return (
    <Tooltip title={scope?.length ? `Scope: ${scope.join(', ')}` : 'Blocks erasure, anonymization & retention enforcement'}>
      <Tag icon={<LockOutlined />} color="volcano" style={{ fontWeight: 600 }}>
        LEGAL HOLD
      </Tag>
    </Tooltip>
  );
};
